import { useState } from 'react';
import { Plus, Pencil, Trash2, DoorOpen, LayoutDashboard } from 'lucide-react';
import { ROOM_TYPES, type Room, type CreateRoomInput, type UpdateRoomInput } from '@peles/shared';
import { EntityList, StatusBadge } from '../common';
import { EntityModal } from '../common/EntityModal';
import type { EntityListColumn } from '../common';
import { useRooms, useCreateRoom, useUpdateRoom, useDeleteRoom } from '../../hooks/useFloorPlans';

interface RoomFormState {
  name: string;
  roomType: string;
  widthFt: string;
  heightFt: string;
  maxCapacity: string;
}

const EMPTY_FORM: RoomFormState = {
  name: '',
  roomType: '',
  widthFt: '',
  heightFt: '',
  maxCapacity: '',
};

interface Props {
  venueId: string;
}

export function VenueRoomListView({ venueId }: Props) {
  const { data: rooms = [], isLoading } = useRooms(venueId);
  const createRoom = useCreateRoom();
  const updateRoom = useUpdateRoom();
  const deleteRoom = useDeleteRoom();

  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [form, setForm] = useState<RoomFormState>(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  function openCreate() {
    setEditingRoom(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  }

  function openEdit(room: Room) {
    setEditingRoom(room);
    setForm({
      name: room.name,
      roomType: room.roomType ?? '',
      widthFt: String(room.widthFt),
      heightFt: String(room.heightFt),
      maxCapacity: room.maxCapacity != null ? String(room.maxCapacity) : '',
    });
    setError('');
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRoom(null);
    setError('');
  }

  function setField(key: keyof RoomFormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setError('');
    const name = form.name.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }
    const widthFt = parseFloat(form.widthFt);
    const heightFt = parseFloat(form.heightFt);
    if (isNaN(widthFt) || widthFt <= 0) {
      setError('Width must be a positive number.');
      return;
    }
    if (isNaN(heightFt) || heightFt <= 0) {
      setError('Height must be a positive number.');
      return;
    }

    const maxCapacity = form.maxCapacity ? parseInt(form.maxCapacity, 10) : undefined;
    if (form.maxCapacity && isNaN(maxCapacity!)) {
      setError('Capacity must be a number.');
      return;
    }

    try {
      if (editingRoom) {
        const updates: UpdateRoomInput = { name, widthFt, heightFt };
        if (form.roomType) updates.roomType = form.roomType;
        else updates.roomType = null;
        if (maxCapacity != null) updates.maxCapacity = maxCapacity;
        else updates.maxCapacity = null;
        await updateRoom.mutateAsync({ id: editingRoom.id, updates });
      } else {
        const input: CreateRoomInput = { venueId, name, widthFt, heightFt };
        if (form.roomType) input.roomType = form.roomType;
        if (maxCapacity != null) input.maxCapacity = maxCapacity;
        await createRoom.mutateAsync(input);
      }
      closeModal();
    } catch {
      setError('Failed to save room. Please try again.');
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteRoom.mutateAsync(id);
    } catch {
      // deletion failure is non-blocking; room list will refetch
    } finally {
      setDeletingId(null);
    }
  }

  const columns: EntityListColumn<Room>[] = [
    {
      key: 'name',
      header: 'Room',
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => (
        <div className="flex items-center gap-2">
          <LayoutDashboard size={14} className="text-gray-400 flex-shrink-0" />
          <span className="font-medium text-gray-900">{r.name}</span>
        </div>
      ),
    },
    {
      key: 'roomType',
      header: 'Type',
      hideOnMobile: true,
      render: (r) =>
        r.roomType ? (
          <StatusBadge label={r.roomType} color="blue" />
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'dimensions',
      header: 'Size (ft)',
      hideOnMobile: true,
      render: (r) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {r.widthFt} × {r.heightFt}
        </span>
      ),
    },
    {
      key: 'maxCapacity',
      header: 'Capacity',
      hideOnMobile: true,
      render: (r) => (
        <span className="text-sm text-gray-600">{r.maxCapacity != null ? r.maxCapacity : '—'}</span>
      ),
    },
    {
      key: 'entries',
      header: 'Entries',
      hideOnMobile: true,
      render: (r) =>
        r.entries.length > 0 ? (
          <span className="flex items-center gap-1 text-sm text-gray-500">
            <DoorOpen size={13} />
            {r.entries.length}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-20',
      align: 'right',
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEdit(r);
            }}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            title="Edit room"
          >
            <Pencil size={14} />
          </button>
          {deletingId === r.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(r.id);
                }}
                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingId(null);
                }}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeletingId(r.id);
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete room"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const isSaving = createRoom.isPending || updateRoom.isPending;

  return (
    <>
      <EntityList<Room>
        title="Rooms"
        items={rooms}
        columns={columns}
        getKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No rooms yet. Add a room to get started."
        emptyIcon={<LayoutDashboard size={32} className="text-gray-300" />}
        titleActions={
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={15} />
            Add Room
          </button>
        }
      />

      <EntityModal
        open={showModal}
        onClose={closeModal}
        title={editingRoom ? `Edit Room: ${editingRoom.name}` : 'Add Room'}
        size="sm"
      >
        <div className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Room Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g. Main Ballroom"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
            <select
              value={form.roomType}
              onChange={(e) => setField('roomType', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              <option value="">— None —</option>
              {ROOM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Width (ft) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="0.5"
                value={form.widthFt}
                onChange={(e) => setField('widthFt', e.target.value)}
                placeholder="100"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (ft) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="0.5"
                value={form.heightFt}
                onChange={(e) => setField('heightFt', e.target.value)}
                placeholder="80"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
            <input
              type="number"
              min="1"
              value={form.maxCapacity}
              onChange={(e) => setField('maxCapacity', e.target.value)}
              placeholder="e.g. 300"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={closeModal}
              disabled={isSaving}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : editingRoom ? 'Save Changes' : 'Add Room'}
            </button>
          </div>
        </div>
      </EntityModal>
    </>
  );
}
